// Layer 4 (UI) — the project home (polish.P23 #710, locked comp
// `.impeccable/mocks/decision/project-home.html` A, with B's plant-model header
// fact and C's cursor-anchored chat times). The project's front door: its
// identity and design basis, one module index in the persona's rail order with
// each owning route's lead figure as a reference, the chats beside it, and one
// Files panel built from the ingestion seed's artefacts.
// Renders `ProjectHomeScene.transform.ts` and `ProjectFiles.transform.ts`; derives nothing.

import { Fragment, useEffect, useMemo, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, MessagesSquare, Upload } from 'lucide-react';
import { usePersona } from '@/app/providers';
import {
  EmptyState,
  GlassCard,
  PageHeader,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { HelpAnchor } from '@/design-system/HelpAnchor';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { projectTileBySlug, plantPhrase } from '@/versions/v1/shared/project-seed';
import { projectModulesSeed } from '@/versions/v1/shared/project-modules-seed';
import {
  PROJECT_SIDEBAR_NAV_ITEMS,
  projectSidebarHref,
} from '@/versions/v1/shared/project-sidebar-nav';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useClaims } from '@/versions/v1/shared/ClaimsProvider';
import { useEscalationDecisions } from '@/versions/v1/shared/EscalationDecisionProvider';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useAdvisoryFlowRead } from '@/versions/v1/shared/useAdvisoryFlowRead';
import {
  filesLinkToIngestion,
  projectChats,
  projectHomeHeader,
  projectModuleIndex,
  type IndexRun,
  type ModuleIndexRow,
  type ProjectChatRow,
  type ProjectHomeHeader,
} from './ProjectHomeScene.transform';
import { projectFilesPanel, type ProjectFilesPanel } from './ProjectFiles.transform';
import { useWorkflowCatalogueRows } from './useWorkflowCatalogueRows';
import { GATE_PRIMARY_BUTTON } from '../advisory/gate-ui';

const RUN_TONE: Record<NonNullable<IndexRun['tone']>, string> = {
  ok: 'text-[var(--state-online)]',
  warn: 'text-[var(--state-warning)]',
  bad: 'text-[var(--state-blocked)]',
};

/** Row hover is a surface step, never a lift (DESIGN § Motion). */
const ROW_HOVER = 'transition-colors duration-150 hover:bg-[var(--surface-1)] motion-reduce:transition-none';
const ROW_RULE = 'border-b border-[var(--color-divider)] last:border-b-0';
const CAPS = 'text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';

function Runs({ runs }: { runs: readonly IndexRun[] }) {
  return (
    <>
      {runs.map((run, index) =>
        run.strong || run.tone ? (
          <span
            key={index}
            className={cn(run.strong && 'font-semibold', run.tone ? RUN_TONE[run.tone] : 'text-ink-secondary')}
          >
            {run.text}
          </span>
        ) : (
          <Fragment key={index}>{run.text}</Fragment>
        ),
      )}
    </>
  );
}

/** A flush card: title and meta on a hairline header, rows edge to edge (comp `.card.flush`). */
function HomePanel({
  title,
  meta,
  className,
  children,
}: {
  title: string;
  meta?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <GlassCard as="section" tone="matte" aria-label={title} className={cn('flex min-w-0 flex-col overflow-hidden p-0', className)}>
      <header className="flex items-center gap-2 border-b border-[var(--hairline)] px-4 pb-2.5 pt-3">
        <h2 className="text-[13px] font-semibold text-ink-primary">{title}</h2>
        <span className="flex-1" />
        {meta ? <span className="text-[11.5px] font-medium tabular-nums text-muted">{meta}</span> : null}
      </header>
      {children}
    </GlassCard>
  );
}

function Header({ header, actions }: { header: ProjectHomeHeader; actions: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end gap-x-3.5 gap-y-3">
      <div className="mr-auto min-w-0">
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.01em] text-ink-primary">{header.title}</h1>
        <p className="mt-0.5 text-[12.5px] text-ink-secondary">{header.kind}</p>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1.5 tabular-nums">
        {header.facts.map((fact) => (
          <span
            key={fact.id}
            data-header-fact={fact.id}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-2.5 py-[3px] text-xs text-ink-secondary"
          >
            <b className="font-semibold text-ink-primary">{fact.value}</b>
            {fact.id === 'model' ? <span aria-hidden>·</span> : null}
            {fact.rest}
          </span>
        ))}
        {actions}
      </div>
    </header>
  );
}

const ICON_BY_SEGMENT = new Map(PROJECT_SIDEBAR_NAV_ITEMS.map((item) => [item.segment, item.Icon]));

function ModuleIndex({ rows, projectSlug }: { rows: readonly ModuleIndexRow[]; projectSlug: string }) {
  return (
    <HomePanel title="Modules" meta={`${rows.length} on your rail`} className="h-full">
      <nav aria-label="Modules" className="flex flex-col" data-module-index>
        {rows.map((row) => {
          const Icon = ICON_BY_SEGMENT.get(row.segment);
          return (
            <Link
              key={row.segment}
              to={projectSidebarHref(projectSlug, row.segment)}
              data-module-row={row.segment}
              className={cn(
                'grid h-[41px] grid-cols-[128px_150px_116px_minmax(0,1fr)_12px] items-center gap-x-3 px-4',
                ROW_RULE,
                ROW_HOVER,
              )}
            >
              <span data-module-group className={cn(CAPS, 'truncate', row.groupContinues && 'invisible')}>{row.group ?? ''}</span>
              <span className="flex min-w-0 items-center gap-2.5 whitespace-nowrap text-[13px] font-semibold text-ink-primary">
                {Icon ? <Icon aria-hidden className="size-3.5 shrink-0 text-muted" /> : null}
                <span className="truncate">{row.label}</span>
              </span>
              <span data-module-figure className="min-w-0 truncate whitespace-nowrap text-[16px] font-semibold tracking-[-0.005em] tabular-nums text-ink-primary">
                {row.figure}
                {row.unit ? <small className="ml-1 text-[11.5px] font-medium tracking-normal text-muted">{row.unit}</small> : null}
              </span>
              <span
                data-module-comparison
                title={row.comparison.map((run) => run.text).join('')}
                // A long reference wraps to a second line inside the 41 px row
                // rather than ellipsising (two 15 px lines fit).
                className="line-clamp-2 min-w-0 text-[12px] leading-[15px] tabular-nums text-muted"
              >
                <Runs runs={row.comparison} />
              </span>
              <ChevronRight aria-hidden className="size-3 text-muted" />
            </Link>
          );
        })}
      </nav>
    </HomePanel>
  );
}

function Chats({ chats, projectSlug, plant }: { chats: readonly ProjectChatRow[]; projectSlug: string; plant: string }) {
  const chatHref = projectSidebarHref(projectSlug, 'chat');
  return (
    <HomePanel title="Chats" meta={`${chats.length} in this project`}>
      <div className="flex items-center gap-2 border-b border-[var(--hairline)] px-4 py-3">
        <Link
          to={chatHref}
          className="flex h-[34px] min-w-0 flex-1 items-center overflow-hidden whitespace-nowrap rounded-[10px] border border-[var(--hairline-strong)] bg-[var(--surface-1)] px-2.5 text-[12.5px] text-muted"
        >
          <span className="truncate">Ask about {plant}</span>
        </Link>
        <Link to={chatHref} className={cn(GATE_PRIMARY_BUTTON, 'h-[34px] px-3 text-[12.5px]')}>
          Start chat
        </Link>
      </div>
      {chats.length === 0 ? (
        <EmptyState
          embedded
          icon={<MessagesSquare className="h-8 w-8 text-zinc-400" />}
          title="No chats in this project yet"
          description="New chats pick up project context."
          action={
            <Link to={chatHref} className="btn-ghost inline-flex h-9 items-center px-3 text-sm">
              Start your first chat
            </Link>
          }
        />
      ) : (
        <ul className="flex flex-col" data-project-chats>
          {chats.map((chat) => (
            <li key={chat.id} className={ROW_RULE}>
              <Link
                to={chatHref}
                data-chat-row={chat.id}
                className={cn('grid h-[37px] grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5 px-4 text-[12.5px]', ROW_HOVER)}
              >
                <span className="truncate font-semibold text-ink-primary">{chat.title}</span>
                <span className="whitespace-nowrap text-[11.5px] tabular-nums text-muted">{chat.when}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </HomePanel>
  );
}

const FILE_GRID = 'grid grid-cols-[240px_92px_minmax(0,1fr)_140px] items-center gap-x-3.5 px-4';

function FilesPanel({ panel, ingestionHref }: { panel: ProjectFilesPanel; ingestionHref: string | null }) {
  return (
    <HomePanel title="Files" meta={`${panel.total} · ${panel.inModel} in the model`}>
      <div role="table" aria-label="Files" data-project-files>
        <div role="row" className={cn(FILE_GRID, CAPS, 'h-[30px] border-b border-[var(--color-divider)]')}>
          <span role="columnheader">Artefact</span>
          <span role="columnheader">State</span>
          <span role="columnheader">What it put in the model</span>
          <span role="columnheader" className="text-right">Extent</span>
        </div>
        {panel.rows.map((row) => {
          const cells = (
            <>
              <span role="cell" title={row.fullTitle} className={cn('truncate font-semibold', row.inModel ? 'text-ink-primary' : 'text-ink-secondary')}>
                {row.title}
              </span>
              <span role="cell">
                {row.inModel ? (
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-ink-secondary">
                    <i aria-hidden className="size-1.5 rounded-full bg-[var(--state-online)]" />
                    {row.stateLabel}
                  </span>
                ) : (
                  <Pill tone="warning" className="whitespace-nowrap px-2 py-px text-[10px] font-semibold tracking-[0.05em]">
                    {row.stateLabel}
                  </Pill>
                )}
              </span>
              <span role="cell" title={row.contribution} className="truncate text-ink-secondary">{row.contribution}</span>
              <span role="cell" className="truncate whitespace-nowrap text-right tabular-nums">
                {row.extent.figure ? <span className="font-semibold text-ink-primary">{row.extent.figure} </span> : null}
                <span className="text-[11.5px] text-muted">{row.extent.noun}</span>
              </span>
            </>
          );
          const rowClass = cn(FILE_GRID, 'h-10 border-b border-[var(--color-divider)] text-[12.5px]');
          return ingestionHref ? (
            <Link key={row.id} to={ingestionHref} role="row" data-file-row={row.id} className={cn(rowClass, ROW_HOVER)}>
              {cells}
            </Link>
          ) : (
            <div key={row.id} role="row" data-file-row={row.id} className={rowClass}>
              {cells}
            </div>
          );
        })}
      </div>
      <div className="px-4 py-2.5">
        <button type="button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-secondary hover:text-ink-primary">
          <Upload aria-hidden className="size-3" />
          Upload file
        </button>
      </div>
    </HomePanel>
  );
}

export function ProjectHomeScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const { persona } = usePersona();
  const { can } = useRole();
  const { state, phase, diagnosis } = useScenario();
  const advisory = useAdvisory();
  const claims = useClaims();
  const { records } = useEscalationDecisions();
  const advisoryFlow = useAdvisoryFlowRead();
  const project = projectTileBySlug(projectId);
  const slug = project?.slug ?? 'alpha';
  const showMoney = can('money:view');
  // The OptiFlow catalogue belongs to the plant project; any other project's
  // Workflows row reads its own foundation page (ProjectHomeScene.transform).
  const catalogueRows = useWorkflowCatalogueRows(slug);
  const workflowCatalogue = useMemo(
    () => (projectModulesSeed(slug).workflowCatalogue === 'optiflow' ? catalogueRows : []),
    [slug, catalogueRows],
  );

  const header = useMemo(() => (project ? projectHomeHeader(project, claims) : null), [project, claims]);
  const rows = useMemo(
    () =>
      projectModuleIndex(
        {
          persona,
          projectSlug: slug,
          state,
          phase,
          diagnosis,
          advisory,
          fmeaLearning: advisory.fmeaLearning,
          escalations: records,
          showMoney,
          advisoryFlow: advisoryFlow.read,
          workflowCatalogue,
        },
        claims,
      ),
    [persona, slug, state, phase, diagnosis, advisory, records, showMoney, advisoryFlow, workflowCatalogue, claims],
  );
  const chats = useMemo(() => projectChats(slug), [slug]);
  const files = useMemo(() => projectFilesPanel(slug), [slug]);
  const hasIngestion = filesLinkToIngestion(persona);

  useEffect(() => {
    document.title = project ? `${project.name} · OptiFlow` : 'Project · OptiFlow';
  }, [project]);

  if (!project || !header) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <PageHeader title="Project not found" />
          <Link to="/projects" className="text-sm text-accent-600">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProjectHomeScene">
        <SurfacePageBody className="space-y-3.5 pb-6" width="full">
          <Header header={header} actions={<HelpAnchor doc="workspace/project-home" label="Project home" />} />
          {/* Each card sizes to its content: Chats never stretches beside a long index. */}
          <div className="grid grid-cols-12 items-start gap-3.5">
            <Commentable name="ModuleIndex">
              <div className="col-span-12 min-w-0 min-[1400px]:col-span-8">
                <ModuleIndex rows={rows} projectSlug={project.slug} />
              </div>
            </Commentable>
            <Commentable name="ProjectChats">
              <div className="col-span-12 min-w-0 min-[1400px]:col-span-4">
                <Chats chats={chats} projectSlug={project.slug} plant={plantPhrase(project.name)} />
              </div>
            </Commentable>
          </div>
          <Commentable name="ProjectFiles">
            <div>
              <FilesPanel panel={files} ingestionHref={hasIngestion ? projectSidebarHref(project.slug, 'ingestion') : null} />
            </div>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
